import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { trpc } from '@/data-loaders';
import { Check, X, PencilLine } from 'lucide-react';
import { Button } from '@curtin-dxp/curtin-ui';
import { useAwmsStore } from '@/store/AwmsStore';
import { TdActivityValue, TdTableHeader, TdTableRow, TdUpdatedDetails } from '#server/models';
import { useHandleError, useUnitWorkloadData } from '@/hooks';
import { TEACHING_DELIVERY_ROW_HEADERS } from '@/constants';
import { Input } from '@/components/ui/input';
import { IconComponent } from '@/components/Common/Icons';
import { AlertModal, Loader } from '@/components/Common';
import { useHandleSuccess } from '@/hooks/use-handle-success';
import { useNavigate } from 'react-router';
import { formatNumber } from '@/lib/utils';
import { NumberInput } from '@/components/ui';

interface Rows {
  tdRows: TdTableRow[];
  academicTdRows: TdTableRow[];
}

export const UnitTeachingDelivery = ({ unitWorkloadId }: { unitWorkloadId: number }) => {
  const sendToastTeachingDelivery = useHandleSuccess({
    title: 'Update teaching delivery',
  });
  const handleError = useHandleError();
  const setActiveSheet = useAwmsStore((state) => state.setActiveSheet);
  const selectedCampusId = useAwmsStore((state) => state.selectedCampusId);
  const [isTdEditable, setIsTdEditable] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [rowsWithCalculations, setRowsWithCalculations] = useState<Rows>({
    tdRows: [],
    academicTdRows: [],
  });
  const [updatedActivityList, setUpdatedActivityList] = useState<TdUpdatedDetails[]>([]);
  const componentRef = useRef<HTMLDivElement>(null);
  const updatedActivityListRef = useRef(updatedActivityList);
  const [alert, setAlert] = useState<boolean>(false);
  const highlightTableRowCount = [2, 4];
  const { unitTeachingDeliveryDetails, assignedAcademics, isPageLoading } = useUnitWorkloadData(unitWorkloadId);
  const { tableHeaders, rows: tableRows } = unitTeachingDeliveryDetails || {};
  const originalTdDetailsRef = useRef(unitTeachingDeliveryDetails);
  const projectUnitsActivityId =
    tableHeaders?.find((header: TdTableHeader) => header.name.toUpperCase() === 'PROJECT UNITS')?.id || -1;
  const trpcUtils = trpc.useUtils();
  const navigate = useNavigate();
  const setSelectedStaff = useAwmsStore((state) => state.setSelectedStaff);
  const handleInvalidateAndRefetch = async () => {
    // Scope to this unit workload + campus only. An unscoped refetch() would refetch every
    // getTeachingDeliveryDetails entry still in the cache (one per unit/campus visited in the
    // last CACHE_TIME), batching them all into a single GET whose query string exceeds the
    // 2048 byte WAF limit at ~29 calls and is blocked with a 403 in prod.
    // invalidate() already refetches the mounted query. INC0592322
    await trpcUtils.getTeachingDeliveryDetails.invalidate({ unitWorkloadId, campusId: selectedCampusId });
  };

  const updateTdDetailsMutation = trpc.updateTeachingDeliveryDetails.useMutation({
    onSuccess: async () => {
      setUpdatedActivityList([]);
      await handleInvalidateAndRefetch();
      sendToastTeachingDelivery();
    },
    onError: (error) => {
      setUpdatedActivityList([]);
      console.error('Error updating teaching delivery details:', error);
      handleError([error]);
    },
  });

  const calculateActivityHours = (
    rowHeader: string,
    rows: TdTableRow[] | undefined,
    tableHeaders: TdTableHeader[] | undefined,
  ) => {
    if (!rows || rows.length === 0) return [];
    if (rowHeader === 'Can assign') {
      return tableHeaders?.map((header) => {
        const [taughtWeeks, classes, duration] = TEACHING_DELIVERY_ROW_HEADERS.map(
          (rowHeader) =>
            rows
              ?.find((row) => row.rowHeader === rowHeader)
              ?.activityValues.find((value) => value.activityId === header.id)?.value || 0,
        );
        return {
          activityId: header.id,
          value: Number(taughtWeeks) * Number(classes) * Number(duration),
        };
      });
    } else if (rowHeader === 'Assigned') {
      return tableHeaders?.map((header) => {
        const total = rows
          ?.filter((row) => row.rowHeader.includes(', '))
          .reduce((sum, row) => {
            const value = row.activityValues.find((value) => value.activityId === header.id)?.value || 0;
            return sum + Number(value);
          }, 0);

        return {
          activityId: header.id,
          value: total || 0,
        };
      });
    }
    return [];
  };

  // useEffect to calculate and add values for 'Can assign' and 'Assigned' row
  useEffect(() => {
    if (unitTeachingDeliveryDetails) {
      originalTdDetailsRef.current = unitTeachingDeliveryDetails;
      const canAssignHours = calculateActivityHours('Can assign', tableRows?.tdRows, tableHeaders);
      const academicAssignedHours = calculateActivityHours('Assigned', tableRows?.academicTdRows, tableHeaders);

      const updatedRows = {
        tdRows: tableRows?.tdRows?.length
          ? [
              {
                rowHeader: 'Can assign',
                activityValues: canAssignHours ?? [],
              },
              ...(tableRows?.tdRows ?? []),
            ]
          : [],
        academicTdRows: tableRows?.academicTdRows?.length
          ? [
              ...(tableRows?.academicTdRows ?? []),
              {
                rowHeader: 'Assigned',
                activityValues: academicAssignedHours ?? [],
              },
            ]
          : [],
      };

      originalTdDetailsRef.current = {
        ...originalTdDetailsRef.current,
        rows: updatedRows,
      };

      setRowsWithCalculations(updatedRows);
    }
  }, [unitTeachingDeliveryDetails]);

  // useEffect to recalculate rowsWithCalculations when updatedActivityList changes
  useEffect(() => {
    if (isReset || rowsWithCalculations.tdRows.length > 0 || rowsWithCalculations.academicTdRows.length > 0) {
      // Recalculate based on the latest edited activity values
      const latestRowsWithCalculatedValues: Rows = { ...rowsWithCalculations };

      // Recalculate 'Can assign' and 'Assigned' rows
      const updatedCanAssign = latestRowsWithCalculatedValues.tdRows.map((row) => {
        if (row.rowHeader === 'Can assign') {
          row.activityValues =
            calculateActivityHours('Can assign', latestRowsWithCalculatedValues.tdRows, tableHeaders) ?? [];
        }
        return row;
      });

      const updatedAcademicAssigned = latestRowsWithCalculatedValues.academicTdRows.map((row) => {
        if (row.rowHeader === 'Assigned') {
          row.activityValues =
            calculateActivityHours('Assigned', latestRowsWithCalculatedValues.academicTdRows, tableHeaders) ?? [];
        }
        return row;
      });

      setRowsWithCalculations({
        tdRows: updatedCanAssign,
        academicTdRows: updatedAcademicAssigned,
      });

      if (isReset) {
        setIsReset(false);
      }
    }
  }, [isReset, updatedActivityList]);

  // useEffect to handle outside click
  useEffect(() => {
    updatedActivityListRef.current = updatedActivityList;
    const handleClickOutside = (event: MouseEvent, updatedActivityList: TdUpdatedDetails[]) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        if (updatedActivityList.length > 0) setAlert(true);
        else setIsTdEditable(false);
      }
    };

    const handleClickOutsideWithList = (event: MouseEvent) => {
      handleClickOutside(event, updatedActivityList);
    };

    document.addEventListener('mousedown', handleClickOutsideWithList);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideWithList);
    };
  }, [updatedActivityList]);

  const isFirstTdRow = (rowCount: number): boolean => {
    if (rowCount === 1) {
      return true;
    } else {
      return false;
    }
  };

  const isLastAcademicTdRow = (rowCount: number): boolean => {
    if (rowCount === rowsWithCalculations.academicTdRows?.length) {
      return true;
    } else {
      return false;
    }
  };

  const setBottomBorder = (type: string, rowCount: number) => {
    if (type === 'tdRow' && rowCount === highlightTableRowCount[1]) {
      return 'border-b-2 border-deep-blue';
    }
    if (type === 'academicTdRow' && rowCount !== rowsWithCalculations.academicTdRows?.length - 1) {
      return 'border-b-[1px] border-secondary-grey-04';
    }
    if (type === 'academicTdRow' && rowCount === rowsWithCalculations.academicTdRows?.length - 1) {
      return 'border-b-[1px] border-deep-blue';
    }
    if (type === 'academicTdRow' && isLastAcademicTdRow(rowCount)) {
      return 'border-b-[1px] border-secondary-grey-04';
    }
    return '';
  };

  const isOverAssignedHours = (rowCount: number, academicActivity: TdActivityValue) => {
    if (academicActivity.activityId == projectUnitsActivityId) return;

    const canAssignActivity = rowsWithCalculations?.tdRows
      ?.find((row) => row.rowHeader === 'Can assign')
      ?.activityValues.find((tdActivity) => tdActivity.activityId === academicActivity.activityId);
    if (isLastAcademicTdRow(rowCount) && canAssignActivity) {
      return canAssignActivity.value < academicActivity.value;
    }
    return false;
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newValue = Number(event.target.value);
    if (!isNaN(newValue)) {
      newValue = Number(newValue.toFixed(2));
    }
    const rowHeader = event.target.dataset.rowHeader as string;
    const activityId = Number(event.target.dataset.activityId);
    const prevValue = Number(event.target.dataset.prevValue);
    const academicTdActivityId = event.target.dataset.academicTdActivityId
      ? Number(event.target.dataset.academicTdActivityId)
      : undefined;
    const rowsToSearch = academicTdActivityId ? 'academicTdRows' : 'tdRows';
    const originalValue = originalTdDetailsRef.current?.rows?.[rowsToSearch]
      ?.find((row: TdTableRow) => row.rowHeader === rowHeader)
      ?.activityValues.find(
        (activity: TdActivityValue) =>
          activity.activityId === Number(activityId) &&
          (academicTdActivityId ? activity.academicTdActivityId === academicTdActivityId : true),
      )?.value;

    if (isNaN(newValue) || prevValue === newValue) {
      return;
    }

    // update changed value in rowsWithCalculations
    setRowsWithCalculations((prev) => {
      const { tdRows, academicTdRows } = prev;

      const updateRowValues = (activityRows: TdTableRow[]) =>
        activityRows.map((row) =>
          row.rowHeader === rowHeader
            ? {
                ...row,
                activityValues: row.activityValues.map((activity) =>
                  activity.activityId === activityId ? { ...activity, value: newValue } : activity,
                ),
              }
            : row,
        );

      return {
        tdRows: updateRowValues(tdRows),
        academicTdRows: updateRowValues(academicTdRows),
      };
    });

    if (Number(originalValue) !== Number(newValue)) {
      setUpdatedActivityList((prev) => {
        const existingIndex = prev.findIndex(
          (entry) =>
            entry.activity.activityId === activityId &&
            entry.rowHeader === rowHeader &&
            (entry.activity.academicTdActivityId === academicTdActivityId || !academicTdActivityId),
        );

        const updatedEntry = {
          rowHeader,
          activity: {
            activityId,
            value: newValue,
            ...(academicTdActivityId !== undefined && { academicTdActivityId }),
          },
        };

        if (existingIndex >= 0) {
          // Replace the existing entry
          return [...prev.slice(0, existingIndex), updatedEntry, ...prev.slice(existingIndex + 1)];
        } else {
          // Add a new entry
          return [...prev, updatedEntry];
        }
      });
    } else {
      // Remove from updatedActivityList if value is reset to original
      setUpdatedActivityList((prev) =>
        prev.filter(
          (entry) =>
            !(
              entry.rowHeader === rowHeader &&
              entry.activity.activityId === Number(activityId) &&
              (entry.activity.academicTdActivityId === academicTdActivityId || !academicTdActivityId)
            ),
        ),
      );
    }
  };

  const handleCancelEditing = async () => {
    setIsReset(true);
    setUpdatedActivityList([]);
    if (originalTdDetailsRef.current) {
      setRowsWithCalculations(originalTdDetailsRef.current.rows);
    }
  };

  if (isPageLoading) {
    return <Loader />;
  }

  return (
    <>
      <AlertModal
        title="Unsaved changes"
        description="You have unsaved changes.  Please save them before navigating away."
        confirmAction={() => {
          setAlert(false);
        }}
        open={alert}
        confirmLabel="OK"
      />
      <div ref={componentRef} className="flex basis-4/5 flex-col gap-5 bg-white py-5 pr-1">
        <div className="flex flex-row items-center gap-4 pb-4">
          <h3 className="min-h-8 text-xl font-bold">Teaching delivery (TD)</h3>
          {selectedCampusId > 0 ? (
            isTdEditable ? (
              <div className="flex flex-row gap-1">
                <Button
                  size="icon"
                  variant="filled-secondary"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setIsTdEditable(false);
                    handleCancelEditing();
                  }}
                >
                  <IconComponent IconType={X} />
                </Button>{' '}
                <Button
                  size="icon"
                  variant="filled-secondary"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setIsTdEditable(false);
                    updateTdDetailsMutation.mutate({ tdUpdatedDetails: updatedActivityList, unitWorkloadId });
                  }}
                >
                  <IconComponent IconType={Check} />
                </Button>
              </div>
            ) : (
              <Button
                size="icon"
                variant="filled-secondary"
                className="p- h-8 w-8"
                onClick={() => {
                  setIsTdEditable(true);
                }}
              >
                <IconComponent IconType={PencilLine} />
              </Button>
            )
          ) : (
            ''
          )}
        </div>
        {selectedCampusId > 0 ? (
          <div className="table-auto">
            <div className="table-header-group text-sm font-semibold leading-[21px] text-secondary-grey-02">
              <div key="allocation" className="table-cell w-[150px] border-separate py-3 text-left">
                Allocations
              </div>
              {tableHeaders?.map((header: TdTableHeader) => {
                return (
                  <div key={header.id.toString()} className="table-cell w-[150px] border-separate py-3">
                    {header.name}
                  </div>
                );
              })}
            </div>
            {rowsWithCalculations.tdRows?.map((row: TdTableRow, index: number) => {
              const rowCount = index + 1;
              return (
                <div
                  key={'tdRows-' + rowCount.toString()}
                  className={`${rowCount > 1 ? 'bg-deep-blue-50' : ''} table-row`}
                >
                  <div
                    className={`table-cell border-separate py-3 text-left text-sm text-secondary-grey-02 ${
                      isFirstTdRow(rowCount) ? 'font-normal' : 'bg-deep-blue-50 font-semibold' } ${setBottomBorder('tdRow', rowCount)}`}
                  >
                    {row.rowHeader}
                  </div>
                  {row.activityValues.map((activity) => {
                    return (
                      <div
                        key={activity.activityId.toString()}
                        className={`table-cell w-[200px] border-separate py-3 text-left text-secondary-grey-01 ${
                          isFirstTdRow(rowCount) ? 'text-sm font-bold' : 'text-base font-normal' } ${setBottomBorder('tdRow', rowCount)}`}
                      >
                        {activity.activityId !== projectUnitsActivityId ? (
                          <>
                            {isFirstTdRow(rowCount) ? (
                              `${formatNumber(activity.value)} Hours`
                            ) : (
                              <NumberInput
                                value={activity.value}
                                className="disabled h-[30px] w-[117px] bg-white text-sm font-normal leading-5"
                                disabled={!isTdEditable}
                                data-row-header={row.rowHeader}
                                data-activity-id={activity.activityId}
                                data-prev-value={activity.value}
                                onChange={handleInputChange}
                                {...(['Classes', 'Taught weeks'].includes(row.rowHeader) ? {} : { decimalScale: 2 })}
                              />
                            )}
                          </>
                        ) : (
                          ''
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {rowsWithCalculations.academicTdRows.length > 0 ? (
              <>
              {rowsWithCalculations.academicTdRows?.map((row: TdTableRow, index: number) => {
                const rowCount = index + 1;
                return (
                  <div key={'academicTdRows-' + rowCount.toString()} className="table-row">
                    <div
                      className={`table-cell border-separate py-3 text-left ${
                        isLastAcademicTdRow(rowCount)
                          ? 'text-b3 font-normal text-secondary-grey-01'
                          : 'cursor-pointer text-b3 font-semibold text-deep-blue underline'
                        } ${setBottomBorder('academicTdRow', rowCount)}`}
                      onClick={() => {
                        const staff = assignedAcademics?.find(
                          ({ academicWorkload: { lastName, firstName } }) =>
                            `${lastName}, ${firstName}` === row.rowHeader,
                        );
                        setSelectedStaff({
                          id: staff?.academicWorkload.id || 0,
                          firstName: staff?.academicWorkload.firstName ?? '',
                          lastName: staff?.academicWorkload.lastName ?? '',
                        });
                        setActiveSheet('staff-workload');
                        navigate('/staffs');
                      }}
                    >
                      {row.rowHeader}
                    </div>
                    {row.activityValues.map((activity) => {
                      return (
                        <div
                          key={
                            isLastAcademicTdRow(rowCount)
                              ? 'assignedHours_' + activity.activityId
                              : activity.academicTdActivityId
                          }
                          className={`table-cell w-[200px] border-separate py-[23px] text-left text-b3 ${
                            isOverAssignedHours(rowCount, activity) ? 'text-tertiary-red' : 'text-secondary-grey-01' } ${
                            isLastAcademicTdRow(rowCount) ? 'font-bold' : '' } ${setBottomBorder('academicTdRow', rowCount)}`}
                        >
                          {isLastAcademicTdRow(rowCount) ? (
                            Number(activity.value).toFixed(2) + ' Hours'
                          ) : (
                            <NumberInput
                              value={activity.value}
                              className="disabled h-[30px] w-[117px] bg-white text-sm font-normal leading-5"
                              disabled={!isTdEditable}
                              data-row-header={row.rowHeader}
                              data-activity-id={activity.activityId}
                              data-prev-value={activity.value}
                              data-academic-td-activity-id={activity.academicTdActivityId}
                              onChange={handleInputChange}
                              decimalScale={2}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              <div key="academicTdRows-unassigned" className="table-row">
                <div className="table-cell border-separate border-b-[1px] border-secondary-grey-04 py-3 text-left text-b3 font-normal text-secondary-grey-01">
                  Unassigned
                </div>
                {rowsWithCalculations.academicTdRows.at(-1)?.activityValues.map((assigned) => {
                  const canAssign =
                    rowsWithCalculations.tdRows
                      .find((row) => row.rowHeader === 'Can assign')
                      ?.activityValues.find((value) => value.activityId === assigned.activityId)?.value ?? 0;
                  // Same as TRD: no limit (project units, or 0 can-assign hours) means nothing to count down from.
                  const unassigned =
                    assigned.activityId == projectUnitsActivityId || Number(canAssign) == 0
                      ? undefined
                      : Number(canAssign) - Number(assigned.value);
                  return (
                    <div
                      key={'unassignedHours_' + assigned.activityId}
                      className={`table-cell w-[200px] border-separate border-b-[1px] border-secondary-grey-04 py-[23px] text-left text-b3 font-bold ${
                        (unassigned ?? 0) < 0 ? 'text-tertiary-red' : 'text-secondary-grey-01' }`}
                    >
                      {unassigned === undefined ? '-' : unassigned.toFixed(2) + ' Hours'}
                    </div>
                  );
                })}
              </div>
              </>
            ) : (
              <div className="pt-4 text-b2 font-bold">Please assign a staff from unit details screen.</div>
            )}
          </div>
        ) : (
          <div className="text-b2 font-bold">Please select a campus from unit details screen.</div>
        )}
      </div>
    </>
  );
};
